"use client";

import type { DelegationIdentity } from "@icp-sdk/core/identity";
import { Copy, ExternalLink, Loader2, Scan } from "lucide-react";
import { useRouter } from "next/navigation";
import { useCallback, useMemo, useState } from "react";
import SignInModal from "@/components/auth/sign-in-modal";
import ScannerModal from "@/components/shared/scanner-modal";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import {
	Drawer,
	DrawerContent,
	DrawerDescription,
	DrawerHeader,
	DrawerTitle,
} from "@/components/ui/drawer";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { useIdentity } from "@/contexts/IdentityContext";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useLanguage } from "@/hooks/useLanguage";
import { createLogAdapter } from "@/lib/logger/console-adapter";
import type { ScanResolution } from "@/lib/scan/scan-resolver";
import { resolveScannedContent } from "@/lib/scan/scan-resolver";
import { toast } from "sonner";

const uiLog = createLogAdapter("components/shared/global-scan-entry.tsx");

interface GlobalScanEntryProps {
	buttonClassName: string;
	iconClassName: string;
	ariaLabel?: string;
	tooltipLabel?: string;
}

function getScanResolutionText(result: ScanResolution | null) {
	if (!result) return "";
	if (result.kind === "supported_url") return result.url;
	if (result.kind === "btc_bound") return result.address;
	if (result.kind === "btc_external") return result.address;
	if (result.kind === "principal_odin_user") return result.principal;
	return result.displayText;
}

export function GlobalScanEntry({
	buttonClassName,
	iconClassName,
	ariaLabel,
	tooltipLabel,
}: GlobalScanEntryProps) {
	const router = useRouter();
	const { language } = useLanguage();
	const { isMobile } = useIsMobile();
	const { isAuthenticated, setAuthData } = useIdentity();

	const [showScannerModal, setShowScannerModal] = useState(false);
	const [showScanResultModal, setShowScanResultModal] = useState(false);
	const [showSignInModal, setShowSignInModal] = useState(false);
	const [scanResolution, setScanResolution] = useState<ScanResolution | null>(null);
	const [isResolvingScan, setIsResolvingScan] = useState(false);
	const [pendingRoute, setPendingRoute] = useState("");

	const handleCopy = useCallback(
		async (value: string) => {
			if (!value) return;
			await navigator.clipboard.writeText(value);
			toast.success(language === "zh" ? "已复制" : "Copied");
		},
		[language],
	);

	const handleScan = useCallback(async (rawContent: string) => {
		setIsResolvingScan(true);
		try {
			const result = await resolveScannedContent(rawContent);
			setScanResolution(result);
			setShowScanResultModal(true);
		} catch (error) {
			uiLog.error("Failed to resolve scan content", error);
			setScanResolution({
				kind: "lookup_error",
				raw: rawContent,
				displayText: rawContent,
				error: error instanceof Error ? error.message : "Scan resolution failed",
			});
			setShowScanResultModal(true);
		} finally {
			setIsResolvingScan(false);
		}
	}, []);

	const openRoute = useCallback(
		(route: string) => {
			setShowScanResultModal(false);
			router.push(route);
		},
		[router],
	);

	const addressRoute = useMemo(() => {
		if (!scanResolution) return "";

		switch (scanResolution.kind) {
			case "principal_odin_user":
				return `/address/${encodeURIComponent(scanResolution.principal)}`;
			case "btc_bound":
				return `/address/${encodeURIComponent(scanResolution.principal)}?btc=${encodeURIComponent(scanResolution.address)}`;
			case "btc_external":
				return `/address/${encodeURIComponent(scanResolution.address)}`;
			default:
				return "";
		}
	}, [scanResolution]);

	const actionRoute = useMemo(() => {
		if (!scanResolution) return "";

		switch (scanResolution.kind) {
			case "principal_odin_user":
				return `/profile?action=transfer&recipient=${encodeURIComponent(scanResolution.principal)}`;
			case "btc_bound":
				return `/profile?action=transfer&recipient=${encodeURIComponent(scanResolution.principal)}`;
			case "btc_external":
				return `/profile?action=withdraw&address=${encodeURIComponent(scanResolution.address)}`;
			default:
				return "";
		}
	}, [scanResolution]);

	const handleActionClick = useCallback(() => {
		if (!actionRoute) return;
		if (isAuthenticated) {
			openRoute(actionRoute);
			return;
		}
		setPendingRoute(actionRoute);
		setShowSignInModal(true);
	}, [actionRoute, isAuthenticated, openRoute]);

	const handleSignInSuccess = useCallback(
		(newPrincipal: string, address: string, newIdentity: DelegationIdentity) => {
			setAuthData(newPrincipal, address, newIdentity);
			setShowSignInModal(false);
			if (pendingRoute) {
				router.push(pendingRoute);
				setPendingRoute("");
			}
		},
		[pendingRoute, router, setAuthData],
	);

	const actionButtonLabel = useMemo(() => {
		if (!scanResolution || !actionRoute) return "";

		if (scanResolution.kind === "btc_external") {
			return isAuthenticated
				? language === "zh"
					? "提取到该地址"
					: "Withdraw to this address"
				: language === "zh"
					? "登录后提取"
					: "Sign in to withdraw";
		}

		return isAuthenticated
			? language === "zh"
				? "向该地址转账"
				: "Transfer to this address"
			: language === "zh"
				? "登录后转账"
				: "Sign in to transfer";
	}, [actionRoute, isAuthenticated, language, scanResolution]);

	const resultMeta = useMemo(() => {
		if (!scanResolution) {
			return {
				badge: "",
				description: "",
				detail: "",
			};
		}

		switch (scanResolution.kind) {
			case "supported_url":
				return {
					badge: language === "zh" ? "站内链接" : "Site Link",
					description:
						language === "zh"
							? "识别为 Odin 站内链接，可直接跳转到对应页面"
							: "Recognized as an Odin site link that can be opened directly.",
					detail: "",
				};
			case "principal_odin_user":
				return {
					badge: language === "zh" ? "地址结果" : "Address Result",
					description:
						language === "zh"
							? "识别为 Odin 用户身份 ID"
							: "Recognized as an Odin user identity ID.",
					detail: "",
				};
			case "btc_bound":
				return {
					badge: language === "zh" ? "地址结果" : "Address Result",
					description:
						language === "zh"
							? "该 BTC 地址已匹配到 Odin 用户"
							: "This BTC address matched an Odin user.",
					detail:
						language === "zh"
							? `归属地址：${scanResolution.principal}`
							: `Owner: ${scanResolution.principal}`,
				};
			case "btc_external":
				return {
					badge: language === "zh" ? "外部 BTC 地址" : "External BTC Address",
					description:
						language === "zh"
							? "未匹配到 Odin 站内用户，可作为外部地址使用"
							: "No Odin user matched. It can be used as an external address.",
					detail: "",
				};
			case "lookup_error":
				return {
					badge: language === "zh" ? "识别失败" : "Lookup Error",
					description:
						language === "zh"
							? "内容已识别，但地址归属查询失败，请稍后重试"
							: "Content was recognized, but address lookup failed. Please try again later.",
					detail: scanResolution.error,
				};
			case "unrecognized":
			default:
				return {
					badge: language === "zh" ? "未识别类型" : "Unknown Type",
					description:
						language === "zh"
							? "暂未识别为站内链接、身份 ID 或 BTC 地址"
							: "This content is not recognized as a site link, identity ID, or BTC address.",
					detail: "",
				};
		}
	}, [language, scanResolution]);

	const button = (
		<button
			type="button"
			onClick={() => setShowScannerModal(true)}
			className={buttonClassName}
			aria-label={ariaLabel || (language === "zh" ? "打开扫一扫" : "Open scanner")}
		>
			<Scan className={iconClassName} />
			<span className="sr-only">
				{tooltipLabel || (language === "zh" ? "扫一扫" : "Scan")}
			</span>
		</button>
	);

	const resultContent = (
		<div className="space-y-4 p-4 pt-2 sm:p-6 sm:pt-2">
			<div className="rounded-2xl border border-border bg-muted/30 p-4">
				<div className="mb-3 flex items-center justify-between gap-3">
					<div className="inline-flex rounded-full bg-foreground px-3 py-1 text-[11px] font-semibold text-background">
						{resultMeta.badge}
					</div>
					<Button
						type="button"
						variant="ghost"
						size="icon"
						onClick={() => void handleCopy(getScanResolutionText(scanResolution))}
						className="h-8 w-8 shrink-0 rounded-full"
					>
						<Copy className="h-4 w-4" />
					</Button>
				</div>

				<div className="space-y-2">
					<div>
						<p className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
							{language === "zh" ? "识别内容" : "Detected Content"}
						</p>
						<p className="mt-2 break-all font-mono text-sm text-foreground">
							{getScanResolutionText(scanResolution)}
						</p>
					</div>
					<p className="text-sm text-muted-foreground">{resultMeta.description}</p>
					{resultMeta.detail ? (
						<p className="break-all text-xs text-muted-foreground">{resultMeta.detail}</p>
					) : null}
				</div>
			</div>

			{isResolvingScan ? (
				<div className="flex items-center gap-2 text-sm text-muted-foreground">
					<Loader2 className="h-4 w-4 animate-spin" />
					<span>{language === "zh" ? "正在解析..." : "Resolving..."}</span>
				</div>
			) : null}

			<div className="grid gap-3">
				{scanResolution?.kind === "supported_url" ? (
					<Button
						type="button"
						onClick={() => {
							openRoute(scanResolution.internalPath);
						}}
						className="rounded-xl"
					>
						{language === "zh" ? "打开页面" : "Open Page"}
					</Button>
				) : null}

				{addressRoute ? (
					<Button
						type="button"
						variant="outline"
						onClick={() => openRoute(addressRoute)}
						className="rounded-xl"
					>
						<ExternalLink className="h-4 w-4" />
						{language === "zh" ? "查看地址信息" : "View Address Info"}
					</Button>
				) : null}

				{actionRoute ? (
					<Button type="button" onClick={handleActionClick} className="rounded-xl">
						{actionButtonLabel}
					</Button>
				) : null}

				{scanResolution ? (
					<Button
						type="button"
						variant="outline"
						onClick={() => void handleCopy(getScanResolutionText(scanResolution))}
						className="rounded-xl"
					>
						{language === "zh" ? "复制内容" : "Copy"}
					</Button>
				) : null}

				{scanResolution?.kind === "lookup_error" ? (
					<Button
						type="button"
						variant="ghost"
						onClick={() => {
							setShowScanResultModal(false);
							setShowScannerModal(true);
						}}
						className="rounded-xl"
					>
						{language === "zh" ? "重新扫码" : "Scan Again"}
					</Button>
				) : null}
			</div>
		</div>
	);

	const resultContainer = isMobile ? (
		<Drawer open={showScanResultModal} onOpenChange={setShowScanResultModal}>
			<DrawerContent showClose>
				<div className="mx-auto w-full max-w-sm">
					<DrawerHeader>
						<DrawerTitle>{language === "zh" ? "扫码结果" : "Scan Result"}</DrawerTitle>
						<DrawerDescription>
							{language === "zh" ? "已识别内容与可执行操作" : "Detected content and available actions"}
						</DrawerDescription>
					</DrawerHeader>
					{resultContent}
				</div>
			</DrawerContent>
		</Drawer>
	) : (
		<Dialog open={showScanResultModal} onOpenChange={setShowScanResultModal}>
			<DialogContent className="max-w-lg rounded-[2rem] border-none p-0 shadow-2xl">
				<DialogHeader className="p-6 pb-2">
					<DialogTitle>{language === "zh" ? "扫码结果" : "Scan Result"}</DialogTitle>
					<DialogDescription>
						{language === "zh" ? "已识别内容与可执行操作" : "Detected content and available actions"}
					</DialogDescription>
				</DialogHeader>
				{resultContent}
			</DialogContent>
		</Dialog>
	);

	return (
		<>
			{tooltipLabel ? (
				<TooltipProvider>
					<Tooltip>
						<TooltipTrigger asChild>{button}</TooltipTrigger>
						<TooltipContent>{tooltipLabel}</TooltipContent>
					</Tooltip>
				</TooltipProvider>
			) : (
				button
			)}

			<ScannerModal
				open={showScannerModal}
				onOpenChange={setShowScannerModal}
				onScan={handleScan}
			/>

			{resultContainer}

			<SignInModal
				open={showSignInModal}
				onOpenChange={(open) => {
					setShowSignInModal(open);
					if (!open) {
						setPendingRoute("");
					}
				}}
				onSuccess={handleSignInSuccess}
			/>
		</>
	);
}
